import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Tag,
  Spin,
  Alert,
  DatePicker,
  Space,
  Typography,
  Button,
  Tooltip,
  Modal,
  message,
} from "antd";
import {
  HistoryOutlined,
  FileSearchOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { getAllCredits } from "../../../api/accounting";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const CreditHistory = ({ selectedBrand = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });

  // Fetch all credit history for selected brands with pagination
  const fetchAllHistory = useCallback(
    async (page = 1, pageSize = 25) => {
      if (!selectedBrand) return;

      setLoading(true);
      setError(null);
      console.log(selectedBrand, "selectedBrand for credits");

      try {
        // Use the API function to get paginated credit history
        const filters = {
          brand: selectedBrand.id,
        };

        // Use the existing getAllCredits API since there's no dedicated credit history API
        const response = await getAllCredits(filters);
        console.log(response, "paginated data for credit history");

        // Set the credit data from the results
        setHistoryData(response.results || []);

        // Update pagination state (credits API might have different pagination structure)
        setPagination({
          current: response.page || 1,
          pageSize: response.page_size || 25,
          total: response.count || 0,
          totalPages: response.total_pages || 0,
        });

        // Apply date range filter if needed
        if (dateRange && dateRange[0] && dateRange[1]) {
          const startDate = dateRange[0].startOf("day");
          const endDate = dateRange[1].endOf("day");

          const filteredData = (response.results || []).filter((item) => {
            const itemDate = moment(item.history_date);
            return (
              itemDate.isSameOrAfter(startDate) &&
              itemDate.isSameOrBefore(endDate)
            );
          });

          setHistoryData(filteredData);
        }
      } catch (err) {
        console.error("Error fetching all credit history:", err);
        setError("Failed to load credit history. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [selectedBrand, dateRange]
  );

  // Handle pagination change
  const handleTableChange = (pagination) => {
    fetchAllHistory(pagination.current, pagination.pageSize);
  };

  // Fetch credit history when selected brand changes
  useEffect(() => {
    if (!selectedBrand) return;

    fetchAllHistory(1, pagination.pageSize);
  }, [selectedBrand, fetchAllHistory]);

  const columns = [
    {
      title: "Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (date) => moment(date).format("YYYY-MM-DD"),
      sorter: (a, b) =>
        moment(a.issue_date).unix() - moment(b.issue_date).unix(),
      defaultSortOrder: "descend",
      width: 120,
    },
    {
      title: "Credit #",
      dataIndex: "number",
      key: "number",
      render: (number, record) => {
        const partnerName = record.partner?.name || "-";
        return (
          <span>
            {number || "-"}
            {partnerName !== "-" && (
              <div style={{ fontSize: "12px", color: "#888" }}>
                {partnerName}
              </div>
            )}
          </span>
        );
      },
      width: 180,
    },
    {
      title: "Type",
      dataIndex: "credit_type",
      key: "credit_type",
      render: (type) => <Tag color="blue">{type || "Credit"}</Tag>,
      width: 100,
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => `$${parseFloat(amount || 0).toFixed(2)}`,
      sorter: (a, b) => (a.total_amount || 0) - (b.total_amount || 0),
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "is_reversed",
      key: "status",
      render: (isReversed) => (
        <Tag color={isReversed ? "red" : "green"}>
          {isReversed ? "Reversed" : "Active"}
        </Tag>
      ),
      width: 100,
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      render: (notes) => notes || "-",
      width: 200,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Space>
      </div>

      <Spin spinning={loading}>
        {historyData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={historyData}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
            onChange={handleTableChange}
            bordered
          />
        ) : (
          <Alert
            message="No credit data"
            description={
              loading
                ? "Loading credit data..."
                : "No credit data found for the selected criteria."
            }
            type="info"
            showIcon
          />
        )}
      </Spin>
    </div>
  );
};

export default CreditHistory;
